package br.gov.go.pge.permanenciaweb.shared

import kotlinx.serialization.Serializable

/*
 * The authorization vocabulary shared by the server and the site.
 *
 * There is no "role" here on purpose: a role is only a named preset of grants. The server
 * authorizes on capabilities; the site renders the same capabilities in the access editor.
 */

/** One thing an account may do. Scoped capabilities also need a [GrantScope]. */
enum class Capability {
    /** Open the roster, the events tab, the removals history and the WebSocket. */
    VIEW_REPORT,

    /** Manually remove somebody from today's roster (virtual exit) and undo it. */
    MANAGE_ROSTER_CORRECTIONS,

    /** Open the "Liberações" form and create stay permits. */
    CREATE_STAY_PERMIT,

    /** List stay permits. The scope decides whether only your own or everybody's. */
    VIEW_STAY_PERMITS,

    /** Revoke stay permits. The scope decides whether only your own or anybody's. */
    REVOKE_STAY_PERMITS,

    /** Approve/deny accounts and grant/revoke any capability (including your own). */
    MANAGE_ACCESS;

    val isScoped: Boolean
        get() = this == VIEW_STAY_PERMITS || this == REVOKE_STAY_PERMITS
}

/** How far a scoped capability reaches. Non-scoped capabilities always use [GLOBAL]. */
enum class GrantScope {
    /** The capability does not operate on a per-record relation (the default for flags). */
    GLOBAL,

    /** Records created by the signed-in account (the `createdBy` relation). */
    OWN,

    /** Any record. */
    ALL,
}

/** Where a grant row came from. Only [SYNC] grants are managed by the `dim_chefia` push. */
enum class GrantSource {
    /** An admin made the decision directly in the access editor. */
    EXPLICIT,

    /** Written by applying a preset (activation or "apply profile"). */
    PRESET,

    /** Written by the `dim_chefia` sync; auto-revoked when the person leaves the list. */
    SYNC,

    /** Written by a one-time database migration. */
    MIGRATION,
}

/** One atomic grant: a capability plus its scope. This is the unit stored in `user_grants`. */
data class AccessGrant(
    val capability: Capability,
    val scope: GrantScope = GrantScope.GLOBAL,
) {
    init {
        require(capability.isScoped || scope == GrantScope.GLOBAL) {
            "${capability.name} não aceita escopo ${scope.name}"
        }
        require(!capability.isScoped || scope != GrantScope.GLOBAL) {
            "${capability.name} exige escopo OWN ou ALL"
        }
    }

    /** Stable key for set/diff operations in the editor. */
    val key: String get() = "${capability.name}:${scope.name}"
}

/** Named bundles that the UI offers as shortcuts. They are not persisted. */
enum class AccessPreset {
    /** Approved account with the base correction capability; report is always separate. */
    USER,

    /** Base + manage the permits you created. Report is still separate. */
    LEADERSHIP,

    /** Everything, including report and access management. */
    ADMIN;

    val grants: Set<AccessGrant>
        get() = when (this) {
            USER -> setOf(AccessGrant(Capability.MANAGE_ROSTER_CORRECTIONS))
            LEADERSHIP -> USER.grants + setOf(
                AccessGrant(Capability.CREATE_STAY_PERMIT),
                AccessGrant(Capability.VIEW_STAY_PERMITS, GrantScope.OWN),
                AccessGrant(Capability.REVOKE_STAY_PERMITS, GrantScope.OWN),
            )
            ADMIN -> Capability.entries.flatMap { capability ->
                if (capability.isScoped) {
                    listOf(AccessGrant(capability, GrantScope.ALL))
                } else {
                    listOf(AccessGrant(capability))
                }
            }.toSet()
        }
}

/** True when the set contains exactly the grant (capability + scope). */
fun Set<AccessGrant>.grants(capability: Capability, scope: GrantScope = GrantScope.GLOBAL): Boolean =
    contains(AccessGrant(capability, scope))

/** True when the set contains the capability at any allowed scope. */
fun Set<AccessGrant>.grantsAnyScope(capability: Capability): Boolean =
    any { it.capability == capability }

/**
 * The preset whose set matches these grants exactly, or null for a custom combination.
 * Used only for display/hints; authorization never reads the preset.
 */
fun presetOf(grants: Set<AccessGrant>): AccessPreset? =
    AccessPreset.entries.firstOrNull { it.grants == grants }

/** One grant row on the wire. `source` tells the UI which rows are sync-managed. */
@Serializable
data class GrantDto(
    val capability: String,
    val scope: String = GrantScope.GLOBAL.name,
    val source: String = GrantSource.EXPLICIT.name,
    val grantedBy: String? = null,
    val grantedAt: String? = null,
    val revokedBy: String? = null,
    val revokedAt: String? = null,
)
